import React, { useState, useEffect, useMemo, forwardRef } from "react";
import { Tabs, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import BankStatements from "./BankStatements";
import Statements from "./Statements";
import Credits from "./Credits";
import CashPage from "./CashPage";
import CreateCredit from "./Shared/Forms/CreateCredit";
import CreateCash from "./Shared/Forms/CreateCash";
import CreateStatement from "./Shared/Forms/CreateStatement";
import PaymentAllocation from "./Shared/Allocation/PaymentAllocation";
import CreditAllocation from "./Shared/Allocation/CreditAllocation";
import CashAllocation from "./Shared/Allocation/CashAllocation";  
import PartnerTransactions from "./TransactionsAllocations/PartnerTransactions";
import ExcelExportModal from "./Shared/Modals/ExcelExportModal";
import OverPayment from "./TransactionsAllocations/OverPayment";
import OverPaymentAllocation from "./Shared/Allocation/OverPaymentAllocation";
import OverPaymentPage from "./TransactionsAllocations/OverPaymentPage";
const normalize = (str) => str?.replace(/\/+$/, "").toLowerCase();

const PaymentAllocationTab = ({ filters, selectedBrand }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeKey, setActiveKey] = useState(null);
  const [closableTabs, setClosableTabs] = useState([]);
  const [statementId, setStatementId] = useState(null);
  const [paymentTransactionId, setPaymentTransactionId] = useState(null);
  const [creditId, setCreditId] = useState(null);
  const [cashId, setCashId] = useState(null);
  
  // Excel export states
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // References to components for export
  const bankStatementsRef = React.useRef(null);
  const statementsHistoryRef = React.useRef(null);
  const creditsRef = React.useRef(null);
  const overpaymentAllocationRef = React.useRef(null);
  const cashRef = React.useRef(null); // Add cash ref

  
  const paymentSubTabs = [
    {
      key: "bank-statements",
      label: "Bank Statements",
      path: "/accounting/payment-allocation/bank-statements",
    },
    {
      key: "statements-history",
      label: "Statements History",
      path: "/accounting/payment-allocation/statements-history",
    },
    {
      key: "credits",
      label: "Credits",
      path: "/accounting/payment-allocation/credits",
    },
    {
      key: "overpayment",
      label: "Overpayments",
      path: "/accounting/payment-allocation/overpayment",
    },
    {
      key: "create-overpayment",
      label: "Create Overpayment",
      path: "/accounting/payment-allocation/create-overpayment",
    },
    { key: "cash", label: "Cash", path: "/accounting/payment-allocation/cash" },
    {
      key: "create-credit",
      label: "Create Credit",
      path: "/accounting/payment-allocation/create-credit",
    },
    {
      key: "create-cash",
      label: "Create Cash Payment",
      path: "/accounting/payment-allocation/create-cash",
    },
    {
      key: "partner-transactions",
      label: "Partner Transactions",
      path: "/accounting/payment-allocation/partner-transactions",
    },
   
  ];

  useEffect(() => {
    if (!activeKey && paymentSubTabs.length > 0) {
      const matchedTab = paymentSubTabs.find((tab) =>
        normalize(location.pathname).includes(normalize(tab.path))
      );
      setActiveKey(matchedTab?.key || paymentSubTabs[0].key);
      if (!matchedTab) {
        navigate(paymentSubTabs[0].path, { replace: true });
      }
    }
  }, [location.pathname, paymentSubTabs, activeKey, navigate]);

  const onChange = (key) => {
    setActiveKey(key);
    const matchedTab = paymentSubTabs.find((tab) => tab.key === key);
    if (matchedTab) navigate(matchedTab.path);
  };

  const onEdit = (targetKey, action) => {
    if (action === "remove") {
      setClosableTabs(closableTabs.filter((tab) => tab.key !== targetKey));
      setActiveKey("bank-statements");
    }
  };

  const openCreateStatement = () => {
    const newTab = {
      key: "create-statement",
      label: "Create Statement",
      closable: true,
    };
    setClosableTabs([
      ...closableTabs.filter((t) => t.key !== "create-statement"),
      newTab,
    ]);
    setStatementId(null);
    setActiveKey("create-statement");
  };

  const openEditStatement = (id) => {
    const newTab = {
      key: "edit-statement",
      label: "Edit Statement",
      closable: true,
      id,
    };
    setClosableTabs([
      ...closableTabs.filter((t) => t.key !== "edit-statement"),
      newTab,
    ]);
    setStatementId(id);
    setActiveKey("edit-statement");
  };

  const openPaymentAllocation = (transactionId) => {
    const newTab = {
      key: "payment-allocation",
      label: "Payment Allocation",
      closable: true,
      transactionId,
    };
    setClosableTabs([
      ...closableTabs.filter((t) => t.key !== "payment-allocation"),
      newTab,
    ]);
    setPaymentTransactionId(transactionId);
    setActiveKey("payment-allocation");
  };

  const openOverPayment = (overpaymentId) => {
    const newTab = {
      key: "create-overpayment",
      label: "Create Overpayment",
      closable: true,
      transactionId: overpaymentId, // Keep the property name as transactionId for consistency
    };
    setClosableTabs([
      ...closableTabs.filter((t) => t.key !== "create-overpayment"),
      newTab,
    ]);
    setPaymentTransactionId(overpaymentId);
    setActiveKey("create-overpayment");
  };

  const openOverPaymentAllocation = (overpaymentId) => {
    const newTab = {
      key: "overpayment-allocation",
      label: "Overpayment Allocation",
      closable: true,
      transactionId: overpaymentId,
    };
    setClosableTabs([
      ...closableTabs.filter((t) => t.key !== "overpayment-allocation"),
      newTab,
    ]);
    setPaymentTransactionId(overpaymentId);
    setActiveKey("overpayment-allocation");
  };
  
  const openCreditAllocation = (creditId) => {
    const newTab = {
      key: "credit-allocation",
      label: "Credit Allocation",
      closable: true,
      creditId,
    };
    setClosableTabs([
      ...closableTabs.filter((t) => t.key !== "credit-allocation"),
      newTab,
    ]);
    setCreditId(creditId);
    setActiveKey("credit-allocation");
  };

  const openCashAllocation = (cashId) => {
    const newTab = {
      key: "cash-allocation",
      label: "Cash Allocation",
      closable: true,
      cashId,
    };
    setClosableTabs([
      ...closableTabs.filter((t) => t.key !== "cash-allocation"),
      newTab,
    ]);
    setCashId(cashId);
    setActiveKey("cash-allocation");
  };

  const openCreateCredit = (creditId = null) => {
    const newTab = {
      key: "create-credit-tab",
      label: creditId ? "Edit Credit" : "Create Credit",
      closable: true,
      creditId,
    };
    setClosableTabs([
      ...closableTabs.filter((t) => t.key !== "create-credit-tab"),
      newTab,
    ]);
    setCreditId(creditId);
    setActiveKey("create-credit-tab");
    navigate("/accounting/payment-allocation/create-credit");
    
  };

  const handleCancelCreateStatement = () => {
    setClosableTabs(
      closableTabs.filter(
        (tab) => tab.key !== "create-statement" && tab.key !== "edit-statement"
      )
    );
    setActiveKey("bank-statements");
  };

  // Excel export handler
  const handleExportClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // dispatch(setToogleHeader(true)); // Collapse sidebar
    setExportModalVisible(true);
  };

  // Handle export from the modal - determine which component to export from
  const handleExport = async (returnFile = false) => {
    if (activeKey === "bank-statements" && bankStatementsRef.current && bankStatementsRef.current.exportToExcel) {
      return await bankStatementsRef.current.exportToExcel(returnFile);
    } else if (activeKey === "statements-history" && statementsHistoryRef.current && statementsHistoryRef.current.exportToExcel) {
      return await statementsHistoryRef.current.exportToExcel(returnFile);
    } else if (activeKey === "credits" && creditsRef.current && creditsRef.current.exportToExcel) {
      return await creditsRef.current.exportToExcel(returnFile);
    } else if (activeKey === "cash" && cashRef.current && cashRef.current.exportToExcel) {
      return await cashRef.current.exportToExcel(returnFile);
    } else {
      throw new Error("Export function not available");
    }
  };

  const getTabItems = () => {
    const fixedTabs = paymentSubTabs.map((tab) => {
      let content = null;
      switch (tab.key) {
        case "bank-statements":
          content = (
            <BankStatements
              ref={bankStatementsRef}
              filters={filters}
              selectedBrand={selectedBrand}
              onAffectPayment={openPaymentAllocation}
              onAffectOverpayment={openOverPayment}
            />
          );
          break;
        case "statements-history":
          content = (
            <Statements
              ref={statementsHistoryRef}
              filters={filters}
              selectedBrand={selectedBrand}
              onEditStatement={openEditStatement}
            />
          );
          break;
        case "credits":
          content = (
            <Credits
              ref={creditsRef}
              filters={filters}
              selectedBrand={selectedBrand}
              onAffectCredit={openCreditAllocation}
              onCreateCredit={openCreateCredit}
            />
          );
          break;
        case "overpayment":
          content = (
            <OverPayment
              ref={overpaymentAllocationRef}
              filters={filters}
              selectedBrand={selectedBrand}
              onAffectOverpayment={openOverPaymentAllocation}
            />
          );
          break;

        case "cash":
          content = (
            <CashPage
              ref={cashRef} 
              filters={filters}
              selectedBrand={selectedBrand}
              onAffectCash={openCashAllocation}
            />
          );
          break;
        case "create-credit":
          content = (
            <CreateCredit selectedBrand={selectedBrand} creditId={null} />
          );
          break;
        case "create-cash":
          content = <CreateCash selectedBrand={selectedBrand} cashId={null} />;
          break;
        case "partner-transactions":
          content = <PartnerTransactions selectedBrand={selectedBrand} />;
          break;
        default:
          break;
      }

      return {
        key: tab.key,
        label: tab.label,
        closable: false,
        children: content,
      };
    });

    const dynamicTabs = closableTabs.map((tab) => {
      switch (tab.key) {
        case "create-statement":
        case "edit-statement":
          return {
            ...tab,
            children: (
              <CreateStatement
                selectedBrand={selectedBrand}
                statementId={tab.id}
                onCancel={handleCancelCreateStatement}
              />
            ),
          };
        case "payment-allocation":
          return {
            ...tab,
            children: (
              <PaymentAllocation
                transactionId={tab.transactionId}
                selectedBrand={selectedBrand}
              />
            ),
          };
          case "create-overpayment":
          return {
            ...tab,
            children: (
              <OverPaymentPage
              transactionId={tab.transactionId}
              selectedBrand={selectedBrand}
              />
            ),
          };
 
        case "overpayment-allocation":
          return {
            ...tab,
            children: (
              <OverPaymentAllocation
                overpaymentId={tab.transactionId}
                selectedBrand={selectedBrand}
              />
            ),
          };
        case "credit-allocation":
          return {
            ...tab,
            children: (
              <CreditAllocation
                creditId={tab.creditId}
                selectedBrand={selectedBrand}
              />
            ),
          };
        case "cash-allocation":
          return {
            ...tab,
            children: (
              <CashAllocation
                cashId={tab.cashId}
                selectedBrand={selectedBrand}
              />
            ),
          };
        case "create-credit-tab":
          return {
            ...tab,
            children: (
              <CreateCredit
                selectedBrand={selectedBrand}
                creditId={tab.creditId}
                onSuccess={() => {
                  setActiveKey("credits");
                  setClosableTabs((prev) =>
                    prev.filter((t) => t.key !== "create-credit-tab")
                  );
                  navigate("/accounting/payment-allocation/credits");
                }}
              />
            ),
          };
        default:
          return tab;
      }
    });

    return [...fixedTabs, ...dynamicTabs];
  };

  const tabBarExtraContent = useMemo(() => {
  if (activeKey === "bank-statements" || activeKey === "statements-history" || activeKey === "credits" || activeKey === "cash") {
    let pageurl = "/accounting/payment-allocation/bank-statements_create";
    let exportType = "Bank Statements";
    
    if (activeKey === "statements-history") {
      pageurl = "/accounting/payment-allocation/statements-history_create";
      exportType = "Statement History";
    } else if (activeKey === "credits") {
      pageurl = "/accounting/payment-allocation/credits_create";
      exportType = "Credits"; 
    } else if (activeKey === "cash") {
      pageurl = "/accounting/payment-allocation/cash_create";
      exportType = "Cash";
    }
    
    return (
      <div style={{ display: 'flex', gap: 8 }}>
        {/* Excel Export Button - show for bank-statements, statements-history, credits, and cash */}
        <Button
          onClick={handleExportClick}
          icon={<ImageWithBasePath src="assets/img/icons/xls.svg" alt="Excel" style={{ width: 16, height: 16 }} />}
          title={`Export ${exportType} to Excel`}
          style={{ marginRight: 16 }}
        />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openCreateStatement}
          >
            Upload Statement
          </Button>
        
      </div>
    );
  }
  return null;
}, [activeKey]);

  return (
    <div >
      <Tabs
        type="editable-card"
        hideAdd
        onEdit={onEdit}
        activeKey={activeKey}
        onChange={onChange}
        items={getTabItems()}
        size="small"
        tabBarExtraContent={tabBarExtraContent}
        destroyInactiveTabPane
      />
      
      {/* Excel Export Modal */}
      <ExcelExportModal
        visible={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        onExport={handleExport}
        exportType={
          activeKey === "bank-statements" ? "Bank Statements" : 
          activeKey === "statements-history" ? "Statement History" : 
          activeKey === "credits" ? "Credits" : "Cash"
        }
        loading={exportLoading}
      />
    </div>
  );
};

export default PaymentAllocationTab;